import React, { ReactNode } from "react";

import { Close } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  IconButton,
  SxProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Breakpoint } from "@mui/system";

import { IgButton } from "./IgButton";
import { IgTypography } from "./IgTypography";
import { ColorDisabled } from "./theme";

const PREFIX = "IgDialog";

const classes = {
  root: `${PREFIX}-root`,
  dialogTitle: `${PREFIX}-dialogTitle`,
  dialogText1: `${PREFIX}-dialogText1`,
  dialogText2: `${PREFIX}-dialogText2`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  marginLeft: "190px",
  [`& .${classes.dialogTitle}`]: {
    fontSize: "20px",
    fontWeight: 600,
    lineHeight: "unset",
  },
  [`& .${classes.dialogText1}`]: {
    marginTop: "15px",
    marginBottom: "15px",
    color: theme.palette.grey[900],
  },
  [`& .${classes.dialogText2}`]: {
    marginBottom: 15,
  },
}));

interface BaseDialogProps
  extends Omit<DialogProps, "open" | "title" | "content"> {
  showDialog: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  content?: any;
  rawContent?: ReactNode;
  yesButtonText?: ReactNode;
  noButtonText?: string | null;
  handleYesClick?: () => void;
  handleNoClick?: () => void;
  yesButtonDisabled?: boolean;
  handleClose: (event?: any, reason?: any) => void;
  loading?: boolean;
  shouldNotClose?: boolean;
  dialogTitleWidth?: string;
  padding?: string;
  dialogContentWidth?: string;
  hideActions?: boolean;
  minWidth?: string;
  maxWidth?: Breakpoint | false;
  dialogHeight?: string | null;
  showCloseIcon?: boolean;
  subtitleSx?: SxProps;
  contentSx?: SxProps;
  dialogImage?: ReactNode;
}

/**
 * @see Dialog
 * @see DialogTitle
 * @see DialogContent
 * @see DialogActions
 */
export const IgDialog = ({
  showDialog,
  handleClose,
  handleNoClick,
  title,
  yesButtonText,
  yesButtonDisabled,
  handleYesClick,
  noButtonText,
  loading,
  content,
  rawContent,
  maxWidth,
  minWidth,
  shouldNotClose,
  dialogTitleWidth,
  dialogContentWidth,
  dialogHeight,
  subtitle,
  showCloseIcon,
  hideActions,
  subtitleSx,
  padding,
  contentSx,
  dialogImage,
  ...rest
}: BaseDialogProps) => {
  const onYesClickPress = () => {
    if (handleYesClick) {
      handleYesClick();
    }
    if (!shouldNotClose) {
      handleClose();
    }
  };

  const onNoClickPress = () => {
    if (handleNoClick) {
      handleNoClick();
    } else {
      handleClose();
    }
  };

  return (
    <StyledDialog
      open={showDialog}
      aria-labelledby={`${title}`}
      aria-describedby={`${title}`}
      onClose={handleClose}
      maxWidth={maxWidth || "xs"}
      scroll={"body"}
      PaperProps={{
        style: {
          borderRadius: "15px",
          width: maxWidth ? undefined : "460px",
          height: dialogHeight === null ? undefined : dialogHeight || "275px",
          justifyContent: "center",
        },
      }}
      {...rest}
    >
      <Grid
        container
        direction={"column"}
        alignItems={"center"}
        textAlign={"center"}
        sx={{
          padding: padding || "10px 10px 10px 10px",
          minWidth: minWidth || undefined,
        }}
      >
        {title && (
          <DialogTitle
            sx={{ width: dialogTitleWidth || "90%" }}
            id="dialogBox-title"
            alignItems={"center"}
            className={classes.dialogTitle}
          >
            <Grid
              container
              sx={{ flexWrap: "nowrap" }}
              alignItems={"center"}
              justifyContent={"space-between"}
              columnSpacing={2}
            >
              <Grid container item direction={"column"}>
                {dialogImage && (
                  <Grid item sx={{ marginBottom: "5px" }}>
                    <>{dialogImage}</>
                  </Grid>
                )}
                <Grid container item justifyContent={"center"}>
                  {title}
                </Grid>
              </Grid>
              {showCloseIcon && (
                <Box
                  sx={{
                    position: "absolute",
                    left: "calc(100% - 45px)",
                    top: "5px",
                  }}
                >
                  <IconButton onClick={() => handleClose()}>
                    <Close sx={{ color: ColorDisabled.main }} />
                  </IconButton>
                </Box>
              )}
            </Grid>
          </DialogTitle>
        )}
        {subtitle && (
          <IgTypography sx={{ fontWeight: 600, ...subtitleSx }}>
            <>{subtitle}</>
          </IgTypography>
        )}
        {content && (
          <DialogContent
            sx={{ width: dialogContentWidth || "255px", ...contentSx }}
          >
            <IgTypography>{content}</IgTypography>
          </DialogContent>
        )}
        {rawContent && (
          <DialogContent sx={{ width: dialogContentWidth || "90%" }}>
            {rawContent}
          </DialogContent>
        )}
        {hideActions ? null : (
          <DialogActions>
            <Grid container direction={"column"} rowSpacing={1}>
              <Grid item>
                <IgButton
                  disabled={yesButtonDisabled}
                  loading={loading}
                  variant="contained"
                  onClick={onYesClickPress}
                >
                  {yesButtonText || "Yes, discard my changes"}
                </IgButton>
              </Grid>
              {noButtonText !== null && (
                <Grid item sx={{ marginLeft: "auto", marginRight: "auto" }}>
                  <IgButton
                    disabled={loading}
                    onClick={() => {
                      onNoClickPress();
                    }}
                  >
                    {noButtonText || "Cancel"}
                  </IgButton>
                </Grid>
              )}
            </Grid>
          </DialogActions>
        )}
      </Grid>
    </StyledDialog>
  );
};
